import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { TRANSLATIONS } from "./translations";

const LangContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

const STORAGE_KEY = "alp.lang";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Persist lang preference on the backend so it roams across devices.
// Iter313 · silent — never blocks UI, never surfaces errors.
async function pushLangToBackend(lang) {
  try {
    await axios.patch(`${API}/auth/me/preferences/lang`, { lang },
      { withCredentials: true, timeout: 5000 });
  } catch (_e) {
    // Anonymous / not authed / offline — LocalStorage already saved it.
  }
}

// One-shot hydrate on mount: if the user has a saved lang in their user doc
// and localStorage doesn't already have a fresher value, use the backend
// preference so the app opens in the right language on any device.
async function hydrateLangFromBackend() {
  try {
    const r = await axios.get(`${API}/auth/me`,
      { withCredentials: true, timeout: 5000 });
    const remote = r?.data?.user?.lang;
    if (remote === "en" || remote === "pt" || remote === "es") return remote;
  } catch (_e) { /* anon / offline */ }
  return null;
}

// Iter316 · IP-based fallback. Only called when localStorage is empty AND
// the authenticated user has no preference — never overrides an explicit
// user choice. Silent-fails to null on any error.
async function detectLangFromIP() {
  try {
    const r = await axios.get(`${API}/geo/lang`, { timeout: 4000 });
    const l = r?.data?.lang;
    if (l === "en" || l === "pt" || l === "es") return l;
  } catch (_e) { /* transport failure — fallback stays in navigator lang */ }
  return null;
}

export function LangProvider({ children }) {
  // Iter316 · Track whether the initial `lang` came from an explicit source
  // so the IP-geo lookup only runs when we're still on the default `en`.
  const langSourceRef = useRef("default");
  const [lang, setLangState] = useState(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved === "en" || saved === "pt" || saved === "es") {
        langSourceRef.current = "localStorage";
        return saved;
      }
      // Iter314 · fall back to the browser locale so first-time visitors
      // don't land on EN when their OS/browser is set to PT or ES.
      const nav = (
        typeof navigator !== "undefined"
          ? (navigator.language || navigator.userLanguage || "")
          : ""
      ).toLowerCase();
      if (nav.startsWith("pt")) { langSourceRef.current = "navigator"; return "pt"; }
      if (nav.startsWith("es")) { langSourceRef.current = "navigator"; return "es"; }
    } catch (e) { /* ignore */ }
    return "en";
  });
  // Skip the very first push so we don't fire a needless PATCH before the
  // user has actually changed the language.
  const isFirstRender = useRef(true);

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, lang); } catch (e) { /* ignore */ }
    document.documentElement.lang = lang;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    pushLangToBackend(lang);
  }, [lang]);

  // On first mount pull the remote preference. If it exists AND differs from
  // localStorage, adopt the remote one (backend is the source of truth for
  // "same user on a new device"). If the user is anon AND we're still on
  // the default `en`, fall back to IP geolocation.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const remote = await hydrateLangFromBackend();
      if (cancelled) return;
      if (remote && remote !== lang) {
        // Bypass the push side-effect: mark isFirstRender so the effect
        // won't PATCH the same value back to the backend.
        isFirstRender.current = true;
        setLangState(remote);
        return;
      }
      // Iter316 · anonymous visitor + no explicit signal → try IP geo.
      if (!remote && langSourceRef.current === "default") {
        const geo = await detectLangFromIP();
        if (cancelled) return;
        if (geo && geo !== lang) {
          isFirstRender.current = true;
          setLangState(geo);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLang = useCallback(
    (l) => setLangState(l === "pt" ? "pt" : l === "es" ? "es" : "en"),
    []);

  const t = useCallback((key, params) => {
    // ES falls back to EN if a key isn't translated yet (most app-level keys
    // will stay EN — the landing's ES copy is inline, so the heavy ES lift
    // is already done; dashboards/CRM show EN for ES users as acceptable).
    const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
    let str = dict[key];
    if (str === undefined) str = TRANSLATIONS.en[key] ?? key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        str = str.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
      });
    }
    return str;
  }, [lang]);

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);
